import { Check } from "lucide-react";
import { Link } from "react-router";

import { type Plan, type Price, pricing } from "@/stripe/pricing";
import { Button } from "@/ui/button";
import { cn } from "@/utils/tailwind";

interface PricingPlansProps {
  className?: string;
}

// Present every available curator subscription from Bookmarked's catalog.
//
export function PricingPlans({ className }: PricingPlansProps) {
  const options = pricing.flatMap((plan) =>
    plan.prices
      .filter((price) => price.available && price.mode === "subscription")
      .map((price) => ({ plan, price })),
  );

  if (options.length === 0) {
    return (
      <p
        className={cn(
          "bg-card text-muted-foreground rounded-2xl border p-7 text-center",
          className,
        )}
      >
        Curator pricing is not available right now. Please check back soon.
      </p>
    );
  }

  return (
    <div className={className}>
      <div className="grid gap-5 md:grid-cols-2">
        {options.map(({ plan, price }) => (
          <PricingCard key={price.id} plan={plan} price={price} />
        ))}
      </div>

      {publicEnv.stripeTrialDays > 0 && (
        <p className="text-muted-foreground mt-6 text-center text-sm">
          Try the complete Studio free for {publicEnv.stripeTrialDays} days. No
          payment method is required to start.
        </p>
      )}
    </div>
  );
}

interface PricingCardProps {
  plan: Plan;
  price: Price;
}

// Keep each billing frequency attached to the same complete curator product.
//
function PricingCard({ plan, price }: PricingCardProps) {
  const isAnnual = plan.name.toLowerCase().includes("annual");
  const buttonLabel =
    publicEnv.stripeTrialDays > 0 ? "Start free trial" : "Choose this plan";
  const signupSearchParams = new URLSearchParams({
    priceId: price.id,
    redirectTo: "/billing",
  });

  return (
    <article
      className={cn(
        "bg-card relative flex flex-col rounded-[2rem] border p-7 shadow-sm sm:p-8",
        isAnnual && "border-signal-strong",
      )}
    >
      {isAnnual && (
        <span className="bg-signal text-signal-foreground absolute top-0 right-7 -translate-y-1/2 rounded-full px-3 py-1 text-[0.68rem] font-bold tracking-[0.1em] uppercase">
          Annual
        </span>
      )}
      <p className="text-signal-strong text-xs font-bold tracking-[0.13em] uppercase">
        {plan.name}
      </p>
      <h3 className="font-display mt-4 text-4xl font-semibold tracking-[-0.04em]">
        {price.name}
      </h3>
      {price.description && (
        <p className="text-muted-foreground mt-2 text-sm leading-6">
          {price.description}
        </p>
      )}

      <ul className="text-muted-foreground mt-7 grid gap-3 text-sm">
        <PricingFeature>One verified public curator profile</PricingFeature>
        <PricingFeature>
          A private Inbox and deliberate public collections
        </PricingFeature>
        <PricingFeature>Plan and payment controls in Billing</PricingFeature>
      </ul>

      <Button
        asChild
        className="mt-8 h-11 rounded-full"
        variant={isAnnual ? "default" : "outline"}
      >
        <Link to={`/login?${signupSearchParams}`}>
          {buttonLabel}
          <span aria-hidden="true">→</span>
        </Link>
      </Button>
    </article>
  );
}

// Keep plan inclusions visually consistent across every pricing card.
//
function PricingFeature({ children }: { children: string }) {
  return (
    <li className="flex items-start gap-2.5">
      <Check className="text-success mt-0.5 size-4 shrink-0" />
      {children}
    </li>
  );
}
